import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical, Navigation, Image, ToggleLeft, Sparkles } from 'lucide-react';

// ============================================================================
// Section defined OUTSIDE component — stable identity prevents input remounting
// ============================================================================
const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
          padding: '12px 16px', border: 'none', background: '#f8f9fa',
          cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151',
          textAlign: 'left',
        }}
      >
        <Icon size={14} style={{ color: '#6b7280' }} />
        <span style={{ flex: 1 }}>{title}</span>
        <span style={{ fontSize: '10px', color: '#9ca3af', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}>&#9660;</span>
      </button>
      {open && <div style={{ padding: '12px 16px' }}>{children}</div>}
    </div>
  );
};

const DEFAULT_ATTRS = {
  logoUrl: '',
  brandName: 'BRAND',
  navLinks: [
    { label: 'Home', path: '/' },
    { label: 'Shop', path: '/shop' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'Orders', path: '/previous_order' },
  ],
  showUserProfile: true,
  showCartIcon: true,
  showWishlistIcon: true,
  mobileMenuBanner: '',
  enableScrollEffect: true,
};

export default function NavbarConfigPanel({ attributes = {}, onAttributeChange, onClose, onImageUpload }) {
  // Local state for immediate input responsiveness — avoids stale closure issues
  const [local, setLocal] = useState(() => ({ ...DEFAULT_ATTRS, ...attributes }));

  // Refs for stable callbacks (never stale)
  const onChangeRef = useRef(onAttributeChange);
  onChangeRef.current = onAttributeChange;
  const timerRef = useRef(null);
  const pendingRef = useRef(null);

  // Sync from parent only when no pending local changes (avoids overwriting typing)
  useEffect(() => {
    if (!timerRef.current) {
      setLocal(prev => ({ ...DEFAULT_ATTRS, ...prev, ...attributes }));
    }
  }, [attributes]);

  // Debounced parent notification
  const notifyParent = useCallback((newAttrs) => {
    pendingRef.current = newAttrs;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) onChangeRef.current(pending);
    }, 150);
  }, []);

  // Flush pending changes (on close or unmount)
  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      if (pendingRef.current) {
        onChangeRef.current(pendingRef.current);
        pendingRef.current = null;
      }
    }
  }, []);

  useEffect(() => () => flush(), [flush]);

  // Generic key-value update
  const update = useCallback((key, value) => {
    setLocal(prev => {
      const next = { ...prev, [key]: value };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  // Nav link updates
  const updateLink = useCallback((index, field, value) => {
    setLocal(prev => {
      const newLinks = [...(prev.navLinks || [])];
      newLinks[index] = { ...newLinks[index], [field]: value };
      const next = { ...prev, navLinks: newLinks };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const addLink = useCallback(() => {
    setLocal(prev => {
      const next = { ...prev, navLinks: [...(prev.navLinks || []), { label: 'New Page', path: '/new-page' }] };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const removeLink = useCallback((index) => {
    setLocal(prev => {
      const next = { ...prev, navLinks: (prev.navLinks || []).filter((_, i) => i !== index) };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  const moveLink = useCallback((from, to) => {
    setLocal(prev => {
      const links = [...(prev.navLinks || [])];
      if (to < 0 || to >= links.length) return prev;
      const [moved] = links.splice(from, 1);
      links.splice(to, 0, moved);
      const next = { ...prev, navLinks: links };
      notifyParent(next);
      return next;
    });
  }, [notifyParent]);

  // Destructure from local state (always up-to-date)
  const {
    logoUrl = '',
    brandName = 'BRAND',
    navLinks = [],
    showUserProfile = true,
    showCartIcon = true,
    showWishlistIcon = true,
    enableScrollEffect = true,
  } = local;

  // Handle close — flush pending changes first
  const handleClose = useCallback(() => {
    flush();
    onClose?.();
  }, [flush, onClose]);

  return (
    <div style={{
      width: '320px', background: '#ffffff', borderLeft: '1px solid #e5e7eb',
      height: '100%', display: 'flex', flexDirection: 'column', minWidth: '320px',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', background: '#1e293b', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Navigation size={16} />
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Navigation Bar Settings</span>
        </div>
        <button onClick={handleClose} style={{
          background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', borderRadius: '4px',
        }}>
          <X size={16} />
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* Brand Section */}
        <Section title="Brand" icon={Image}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Logo Image</label>
            {logoUrl && (
              <div style={{ marginBottom: '8px', padding: '8px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
                <img src={logoUrl} alt="Logo" style={{ maxHeight: '40px', objectFit: 'contain' }} />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onImageUpload) {
                  onImageUpload(file, 'navbar_logo').then(url => {
                    if (url) update('logoUrl', url);
                  });
                } else if (file) {
                  const url = URL.createObjectURL(file);
                  update('logoUrl', url);
                }
              }}
              style={{ fontSize: '11px', width: '100%' }}
            />
            {logoUrl && (
              <button onClick={() => update('logoUrl', '')} style={{
                marginTop: '4px', fontSize: '11px', color: '#ef4444', background: 'none',
                border: 'none', cursor: 'pointer', padding: '2px 0',
              }}>Remove logo</button>
            )}
          </div>
          <div>
            <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Brand Name</label>
            <input
              type="text"
              value={brandName}
              onChange={(e) => update('brandName', e.target.value)}
              placeholder="Your Brand Name"
              style={{
                width: '100%', padding: '6px 10px', borderRadius: '6px',
                border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none',
              }}
            />
          </div>
        </Section>

        {/* Navigation Links */}
        <Section title="Navigation Links" icon={Navigation}>
          {navLinks.map((link, i) => (
            <div key={i} style={{
              display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center',
              padding: '8px', background: '#f9fafb', borderRadius: '8px',
            }}>
              <div style={{ cursor: 'grab', color: '#d1d5db', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <button onClick={() => moveLink(i, i - 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '10px', color: '#9ca3af' }}>&#9650;</button>
                <button onClick={() => moveLink(i, i + 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '10px', color: '#9ca3af' }}>&#9660;</button>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  value={link.label}
                  onChange={(e) => updateLink(i, 'label', e.target.value)}
                  placeholder="Label"
                  style={{
                    width: '100%', padding: '4px 8px', borderRadius: '4px',
                    border: '1px solid #e5e7eb', fontSize: '12px', marginBottom: '4px', outline: 'none',
                  }}
                />
                <input
                  value={link.path}
                  onChange={(e) => updateLink(i, 'path', e.target.value)}
                  placeholder="/page-link"
                  style={{
                    width: '100%', padding: '4px 8px', borderRadius: '4px',
                    border: '1px solid #e5e7eb', fontSize: '11px', color: '#6b7280', outline: 'none',
                  }}
                />
              </div>
              <button onClick={() => removeLink(i)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px',
              }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button onClick={addLink} style={{
            width: '100%', padding: '8px', borderRadius: '8px', border: '1px dashed #d1d5db',
            background: '#fafafa', cursor: 'pointer', fontSize: '12px', color: '#6b7280',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          }}>
            <Plus size={14} /> Add Link
          </button>
        </Section>

        {/* Icon Toggles */}
        <Section title="Show / Hide Icons" icon={ToggleLeft}>
          {[
            { key: 'showUserProfile', label: 'User Profile', value: showUserProfile },
            { key: 'showCartIcon', label: 'Shopping Cart', value: showCartIcon },
            { key: 'showWishlistIcon', label: 'Wishlist (Heart)', value: showWishlistIcon },
          ].map(({ key, label, value }) => (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 0', borderBottom: '1px solid #f3f4f6',
            }}>
              <span style={{ fontSize: '12px', color: '#374151' }}>{label}</span>
              <label style={{ position: 'relative', display: 'inline-block', width: '36px', height: '20px' }}>
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => update(key, e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '10px',
                  background: value ? '#4f46e5' : '#d1d5db', transition: 'background 0.2s',
                }}>
                  <span style={{
                    position: 'absolute', content: '', height: '16px', width: '16px',
                    left: value ? '18px' : '2px', bottom: '2px', background: '#fff',
                    borderRadius: '50%', transition: 'left 0.2s',
                  }} />
                </span>
              </label>
            </div>
          ))}
        </Section>

        {/* Effects */}
        <Section title="Effects" icon={Sparkles} defaultOpen={false}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0',
          }}>
            <span style={{ fontSize: '12px', color: '#374151' }}>Frosted glass effect on scroll</span>
            <label style={{ position: 'relative', display: 'inline-block', width: '36px', height: '20px' }}>
              <input
                type="checkbox"
                checked={enableScrollEffect}
                onChange={(e) => update('enableScrollEffect', e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '10px',
                background: enableScrollEffect ? '#4f46e5' : '#d1d5db', transition: 'background 0.2s',
              }}>
                <span style={{
                  position: 'absolute', height: '16px', width: '16px',
                  left: enableScrollEffect ? '18px' : '2px', bottom: '2px', background: '#fff',
                  borderRadius: '50%', transition: 'left 0.2s',
                }} />
              </span>
            </label>
          </div>
        </Section>

      </div>
    </div>
  );
}
